import { Component, OnInit, OnDestroy, ViewContainerRef, ViewChild, TemplateRef, Renderer2, ChangeDetectionStrategy, ChangeDetectorRef, effect, WritableSignal, signal, Signal, computed } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ToggleEventNameType, ToggleEventService, ToggleEventType } from '../../../../services/toggle-event.service';
import { TraceDrawerType, TraceRouteDataUpdateType, TraceService } from '../../../../services/trace.service';
import { TraceCreateUndoService } from '../trace-create-undo.service';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { AnimationEvent, animate, state, style, transition, trigger } from '@angular/animations';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TraceCreateConfirmationDialogComponent, TraceCreateConfirmationDialogData } from '../trace-create-confirmation-dialog/trace-create-confirmation-dialog.component';
import { BrowserSnifferService } from '../../../..//services/browser-sniffer.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckbox } from '@angular/material/checkbox';

export enum TraceDrawingToolType {
  ROUTE = 'ROUTE',
  POI = 'POI',
  PAUSE = 'PAUSE',
}
export type TraceDrawingToolActionType = {
  action: string;
  value: string;
}
@Component({
  selector: 'tt-drawing-tools',
  imports: [
    CommonModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckbox,
    MatIconModule,
    MatTooltipModule,
    TranslateModule
  ],
  templateUrl: './drawing-tools.component.html',
  animations: [
    trigger('openClose', [
      state('close', style({
        opacity: 0.0,
        transform: 'scale(0.8)'
      })),
      state('open', style({
        opacity: 1.0,
        transform: 'scale(1.0)'
      })),
      transition('open => close', [
        animate('0.1s cubic-bezier(0.0, 0.0, 0.2, 1)')
      ]),
      transition('close => open', [
        //animate('0.2s cubic-bezier(0.4, 0.0, 0.2, 1)'),
        animate('0.2s cubic-bezier(0.0, 0.0, 0.2, 1)')
      ]),
    ]),
  ],
  standalone: true
})
export class DrawingToolsComponent implements OnInit, OnDestroy {
  @ViewChild('drawingToolsPanel') drawingToolsPanelTemplate: TemplateRef<unknown>;

  public readonly isDrawingToolsComponent: boolean = true;
  private _subscriptions: Subscription;
  public activeDrawingTool: WritableSignal<TraceDrawingToolType> = signal(TraceDrawingToolType.ROUTE);
  public activeDrawingToolInfo: WritableSignal<string> = signal('');
  public activeDrawingToolDisabled: WritableSignal<boolean> = signal(false);
  protected overlayRef: OverlayRef;
  protected isAnimating: WritableSignal<boolean> = signal(false);
  public isOpen: WritableSignal<boolean> = signal(false);
  private _olControlElements: NodeListOf<HTMLElement>;
  private elevationProfileOpenPositionStrategy: PositionStrategy;
  private elevationProfileClosedPositionStrategy: PositionStrategy;
  private phonePositionStrategy: PositionStrategy;
  //public ref_formattedRouteLength: WritableSignal<string> = signal('0 <span>m</span>');
  //public ref_formattedRouteUpwardDistance: WritableSignal<string> = signal('_');
  public ref_hasWayPointsOnMap: WritableSignal<boolean> = signal(false);
  public ref_hasRouteOnMap: WritableSignal<boolean> = signal(false);
  public ref_isRouteWithGeneralInfo: WritableSignal<boolean> = signal(false);
  //public ref_hasRouteToCreateProfile: WritableSignal<boolean> = signal(false);
  //public ref_traceRouteData: WritableSignal<TraceRouteData> = signal(null); 
  public isActive: Signal<boolean> = computed(() => this.traceService.isInEditingMode());
  private _confirmationDialog: MatDialogRef<TraceCreateConfirmationDialogComponent, { data: TraceCreateConfirmationDialogData }>;
  private isElevationProfileOpen: WritableSignal<boolean> = signal(false);

  constructor(
    private _toggleEventService: ToggleEventService,
    private _translateService: TranslateService,
    private _renderer: Renderer2,
    private _dialog: MatDialog,
    protected overlay: Overlay,
    protected viewContainerRef: ViewContainerRef,
    public traceCreateUndoService: TraceCreateUndoService,
    public traceService: TraceService,
    public browserSniffer: BrowserSnifferService
  ) {
    this._confirmationDialog = null;
    this._setReferencedInfo();
    // this._setActiveDrawingToolInfo();

    effect(() => {
      const isPhone = this.browserSniffer.isPhone();
      if (this.overlayRef) {
        this.overlayRef!.updatePositionStrategy((
          this.getPostitionStrategy(isPhone)));
      }
    });
  }

  open() {
    if (this.isOpen() || this.isAnimating()) {
      return;
    }
    if (!this.overlayRef) {
      this.overlayRef = this.createOverlay();
    }

    //when the pause button is active and we then switch from create to view mode, this class stays in pause mode, while the trace service resets it's editing & paused status to false
    //when you afterwards switch back to create mode, we need to sync the service and this class
    this._syncActiveDrawingToolWithServiceSettings();

    const drawerPortal = new TemplatePortal(this.drawingToolsPanelTemplate, this.viewContainerRef);
    this.overlayRef.attach(drawerPortal);
    this.isOpen.set(true);
    this._olControlElements.forEach((elm) => {
      this._renderer.addClass(elm, 'drawing-tools-open');
    });
  }
  close() {
    this.isOpen.set(false);
    this.activeDrawingToolDisabled.set(false);
    this._olControlElements.forEach((elm) => {
      this._renderer.removeClass(elm, 'drawing-tools-open');
    });
  }

  onAnimationEventStart(event: AnimationEvent) {
    this.isAnimating.set(true);
  }

  onAnimationEventEnd(event: AnimationEvent) {
    this.isAnimating.set(false);
    if (event.fromState === 'open' && event.phaseName === 'done') {
      this.overlayRef.detach();
    }
  }

  protected createOverlay(): OverlayRef {
    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      panelClass: ['tt-drawing-tools', 'tt-animate-transition'],
      positionStrategy: this.getPostitionStrategy(this.browserSniffer.isPhone()),
      // scrollStrategy: this.overlay.scrollStrategies.close()
      scrollStrategy: this.overlay.scrollStrategies.noop()
    });
    return this.overlayRef;
  }

  ngOnInit(): void {
    setTimeout(() => {
      const safeTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-top')) || 0;
      const safeBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom')) || 0;
      this.phonePositionStrategy = this.overlay.position().global().start('10px').bottom(`${115 + safeBottom}px`);
      this.elevationProfileOpenPositionStrategy = this.overlay.position().global().centerHorizontally().top(`${235 + safeTop}px`);
      this.elevationProfileClosedPositionStrategy = this.overlay.position().global().centerHorizontally().top((64 + safeTop) + 'px');
    }, 0);


    this.isElevationProfileOpen.set(document.getElementsByTagName('body')[0].classList.contains('elevation-profile-open'));
    this._subscriptions = new Subscription();
    this._subscriptions.add(
      this._toggleEventService.emitter$.subscribe((toggleEvent: ToggleEventType) => {
        if (toggleEvent.forwardedFor === 'drawing-tools') {
          switch (toggleEvent.eventName) {
            case ToggleEventNameType.CLOSE_COMMAND: {
              this.close();
              break;
            }
            case ToggleEventNameType.OPEN_COMMAND: {
              this.open();
              break;
            }
            case ToggleEventNameType.INACTIVE_COMMAND: {
              this._toggleDrawingStatus(false);
              break;
            }
            case ToggleEventNameType.ACTIVE_COMMAND: {
              this._toggleDrawingStatus(true);
              break;
            }
          }
        }

        if (toggleEvent.forwardedFor === "elevation-profile") {
          if (!this.browserSniffer.isPhone()) {
            if (toggleEvent.eventName === ToggleEventNameType.CLOSE_COMMAND || toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND) {
              this.isElevationProfileOpen.set(toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND);
            }
            if (toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND) {
              this.isElevationProfileOpen.set(true);
              if (this.overlayRef) {
                this.overlayRef!.updatePositionStrategy((this.elevationProfileOpenPositionStrategy));
              }
            }
            else {
              this.isElevationProfileOpen.set(false);
              if (this.overlayRef) {
                this.overlayRef!.updatePositionStrategy(this.elevationProfileClosedPositionStrategy);
              }
            }
          }
        }
      })
    );

    this._subscriptions.add(
      this.traceService.updatedTraceRouteData$.subscribe((traceRouteDataUpdateType: TraceRouteDataUpdateType) => {
        //if (this.isActive() || this.ref_traceRouteData !== null) {
        if (this.isActive() || this.ref_hasRouteOnMap() || this.ref_hasWayPointsOnMap()) {
          this._setReferencedInfo();
        }
      })
    );

    // this._subscriptions.add(
    //   this.browserSniffer.subscribeToLayoutChanges().subscribe(() => {
    //     this.setPostitionStrategy();
    //   })
    // );

    this._olControlElements = document.querySelectorAll<HTMLElement>(
      'body, .baselayer-selector-button-container, .elevation-profile-button-container, .legend-button-container, .remarks-select-button-container, .measure-button-container, .trace-select-button-container'
    );
  }

  // private setPostitionStrategy() {
  //   if (this.overlayRef) {
  //     this.overlayRef!.updatePositionStrategy((this.getPostitionStrategy()));
  //   }
  // }
  private getPostitionStrategy(isPhone: boolean) {
    return (isPhone) ? (this.phonePositionStrategy) : (this.isElevationProfileOpen()) ? this.elevationProfileOpenPositionStrategy : this.elevationProfileClosedPositionStrategy;
  }

  private _setReferencedInfo() {
    if (this.isActive()) {
      this.ref_hasWayPointsOnMap.set(this.traceService.hasWayPointsOnMap());
      this.ref_hasRouteOnMap.set(this.traceService.hasRouteOnMap());
      this.ref_isRouteWithGeneralInfo.set(this.traceService.hasTraceRouteWithGeneralInfo());
      //this.ref_hasRouteToCreateProfile = this.traceService.hasRouteToCreateProfile();
      // this.ref_traceRouteData = this.traceService.getTraceRouteData();
    } else {
      this.ref_hasWayPointsOnMap.set(false);
      this.ref_hasRouteOnMap.set(false);
      this.ref_isRouteWithGeneralInfo.set(false);
      //this.ref_hasRouteToCreateProfile = false;
      // this.ref_traceRouteData = TraceRouteData.getEmptyTraceRouteDataObject(this._mapService.getCurrentMap().getView().getProjection().getCode());

      //this.ref_formattedRouteUpwardDistance = '0 m';
    }

  }

  ngAfterViewInit() {
    // after resize from mobile phone dim
    //make async to avoid change dedection error
    setTimeout(() => {
      if (this.isActive()) {
        this._setReferencedInfo();
        this.open();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      // this.overlayRef.dispose();
    }
    this._subscriptions.unsubscribe();
  }

  drawingToolAction(drawingToolAction: TraceDrawingToolActionType) {
    switch (drawingToolAction.action) {
      case 'setDrawertool': {
        this._setActiveDrawingTool(TraceDrawingToolType[drawingToolAction.value]);
        break;
      }
      case 'undoLastChange': {
        this.traceCreateUndoService.undoLastChange();
        break;
      }
      case 'redoLastChange': {
        this.traceCreateUndoService.redoLastChange();
        break;
      }
      case 'setAutoRouting': {
        this._setAutoRouting(drawingToolAction.value === 'true');
        break;
      }
    }
  }

  doneDrawing() {
    this.traceService.openTraceTab(TraceDrawerType.VIEW_ROUTE);
  }

  //add to the eventtrain?
  doneDrawingMobile() {
    this.traceService.closeCreateMobile();
  }

  resetMapData() {
    /*
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }

    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: {
        title: this._translateService.instant('trace.create-route.reset'),
        text: this._translateService.instant('trace.create-route.confirmdelete-all'),
        type: 'resetData'
      },
    });

    this._backEventService.registerOpenedWindow({windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true});

    this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.data && result.data.type === 'resetData') {
    */
    //this will broadcast a REMOVED_DATA_MAP, TraceCreateComponent will listen for that
    this.traceService.resetTraceRouteData(false, true);
    /*    
      }
      this._confirmationDialog = null;
    });
    */
  }

  private _setActiveDrawingTool(newActiveDrawingTool: TraceDrawingToolType) {
    this.activeDrawingTool.set(newActiveDrawingTool);
    this._setActiveDrawingToolInfo();

    this.traceService.setEditingModePaused(this.activeDrawingTool() === TraceDrawingToolType.PAUSE);

    if (this.activeDrawingTool() !== TraceDrawingToolType.PAUSE) {
      this.traceService.setEditingModeType(this.activeDrawingTool() === TraceDrawingToolType.ROUTE);
    }
  }

  private _setActiveDrawingToolInfo() {
    this.activeDrawingToolInfo.set(this._translateService.instant(
      'trace.create-route.tool-info-' + this.activeDrawingTool().toLowerCase() + (this.activeDrawingTool().toLowerCase() === 'route' && this.traceService.autoRouting() ? '-auto' : '')
    ));
  }

  private _syncActiveDrawingToolWithServiceSettings() {
    if (this.activeDrawingTool() !== (this.traceService.isEditingModePaused() ? TraceDrawingToolType.PAUSE : (this.traceService.getEditingModeType() === "LineString" ? TraceDrawingToolType.ROUTE : TraceDrawingToolType.POI))) {
      this._setActiveDrawingTool(this.traceService.getEditingModeType() === "LineString" ? TraceDrawingToolType.ROUTE : TraceDrawingToolType.POI);
    }
  }

  private _toggleDrawingStatus(enable: boolean) {
    if (enable) {
      this._setActiveDrawingTool(TraceDrawingToolType.ROUTE);
      this.activeDrawingToolDisabled.set(false);
    } else {
      this._setActiveDrawingTool(TraceDrawingToolType.PAUSE);
      this.activeDrawingToolDisabled.set(true);
    }
  }

  private _setAutoRouting(setAsChecked: boolean) {
    this.traceService.autoRouting.set(setAsChecked);
    this._setActiveDrawingToolInfo()
  }

}
